import React, { useEffect, useState } from 'react';
import { fileHubApi } from '../api';
import type { DocxPreviewBlock, FileDetail } from '../types';

interface Props {
  file: FileDetail;
  highlightAnchor?: string | null;
  onClose: () => void;
  onDownload: (fileId: string) => void;
  onCopyLink: (file: FileDetail) => void;
  onOpenVersions?: () => void;
  archived?: boolean;
}

function fileKind(file: FileDetail): string {
  const name = file.original_name.toLowerCase();
  const mime = (file.mime_type || '').toLowerCase();
  if (mime.includes('pdf') || name.endsWith('.pdf')) return 'PDF';
  if (mime.includes('sheet') || mime.includes('excel') || name.endsWith('.xlsx') || name.endsWith('.xls')) {
    return 'XLSX';
  }
  if (mime.includes('word') || name.endsWith('.docx') || name.endsWith('.doc')) return 'DOCX';
  if (mime.startsWith('image/')) return 'IMG';
  return 'FILE';
}

function previewParagraphs(text: string): string[] {
  return text
    .split(/\n{2,}|\r\n\r\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

function rightsLabel(file: FileDetail): string {
  if (file.acls.length === 0) return 'Владелец · чтение/запись';
  return file.acls
    .map((acl) => {
      const who =
        acl.scope_type === 'user'
          ? 'Этот пользователь'
          : acl.scope_type === 'role'
            ? acl.scope_id
            : acl.scope_type;
      const canDownload = acl.permissions.split(',').includes('download');
      return canDownload ? `${who}: просмотр и скачивание` : `${who}: ${acl.permissions}`;
    })
    .join(' · ');
}

function DocxPreview({ file }: { file: FileDetail }) {
  const [blocks, setBlocks] = useState<DocxPreviewBlock[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setBlocks(null);
    setFailed(false);
    fileHubApi
      .previewFile(file.file_id, file.current_version_id || undefined)
      .then((preview) => {
        if (cancelled) return;
        if (preview.kind !== 'docx' || preview.blocks.length === 0) {
          setFailed(true);
          return;
        }
        setBlocks(preview.blocks);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [file.file_id, file.current_version_id]);

  if (failed) {
    return (
      <div className="file-storage-doc-paper">
        <div className="file-storage-doc-heading">{file.display_name}</div>
        <p className="file-storage-doc-p">
          Не удалось открыть документ Word во встроенном просмотре. Его можно скачать кнопкой «Скачать
          оригинал».
        </p>
      </div>
    );
  }
  if (!blocks) {
    return <p className="file-storage-doc-p">Открываем файл…</p>;
  }
  return (
    <div className="file-storage-doc-paper">
      {blocks.map((block, index) =>
        block.kind === 'table' ? (
          <table key={index} className="file-storage-preview-table">
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p key={index} className="file-storage-doc-p">
            {block.text}
          </p>
        )
      )}
    </div>
  );
}

function StoredFilePreview({ file, kind }: { file: FileDetail; kind: 'IMG' | 'PDF' }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = '';
    setUrl(null);
    setFailed(false);

    fileHubApi
      .downloadFile(file.file_id)
      .then((meta) => fileHubApi.downloadFileStream(file.file_id, meta.version_id))
      .then((blob) => {
        const nextUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(nextUrl);
          return;
        }
        objectUrl = nextUrl;
        setUrl(nextUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file.file_id, file.current_version_id]);

  if (failed) {
    return (
      <p className="file-storage-doc-p">
        Не удалось открыть файл во встроенном просмотре. Его можно скачать кнопкой «Скачать оригинал».
      </p>
    );
  }
  if (!url) {
    return <p className="file-storage-doc-p">Открываем файл…</p>;
  }
  if (kind === 'IMG') {
    return <img className="file-storage-image-preview" src={url} alt={file.display_name} />;
  }
  return <iframe className="file-storage-pdf-frame" title={file.display_name} src={url} />;
}

/** Inline document viewer — template Block5 viewer(), wired to backend file detail. */
export const InlineViewerModal: React.FC<Props> = ({
  file,
  highlightAnchor,
  onClose,
  onDownload,
  onCopyLink,
  onOpenVersions,
  archived = false,
}) => {
  const kind = fileKind(file);
  const preview = file.file_content_preview?.trim() || '';
  const paragraphs = preview ? previewParagraphs(preview) : [];
  const currentVersion = file.versions.find((v) => v.is_current) || file.versions[0];
  const aclLabel = rightsLabel(file);

  return (
    <div data-testid="inline-viewer-modal">
      <div className="file-storage-breadcrumb">
        Файлы и хранилище · <b>{archived ? 'Архивный просмотр' : 'Встроенный просмотр'}</b>
      </div>

      <div className="file-storage-viewer-shell">
        <section className="file-storage-viewer">
          <div className="file-storage-viewer-bar">
            <strong>
              {archived ? <span className="file-storage-chip private">Архивный снимок</span> : null}{' '}
              {file.display_name}
            </strong>
            <span className="file-storage-chip">{kind}</span>
          </div>
          <div className="file-storage-viewer-pane">
            {highlightAnchor ? (
              <div className="file-storage-doc-note" style={{ marginBottom: 14 }}>
                Подсвечен фрагмент первоисточника: {highlightAnchor}
              </div>
            ) : null}

            {kind === 'PDF' || kind === 'IMG' ? (
              <StoredFilePreview file={file} kind={kind} />
            ) : kind === 'DOCX' && file.original_name.toLowerCase().endsWith('.docx') ? (
              <DocxPreview file={file} />
            ) : (
              <div className="file-storage-doc-paper">
                <div className="file-storage-doc-heading">{file.display_name}</div>
                {paragraphs.length ? (
                  paragraphs.map((p, idx) => (
                    <p key={idx} className="file-storage-doc-p">
                      {p}
                    </p>
                  ))
                ) : (
                  <>
                    <p className="file-storage-doc-p">
                      Предпросмотр содержимого недоступен для этого типа. В рабочей версии используется
                      безопасный сервис рендера и преобразования.
                    </p>
                    <p className="file-storage-doc-p">
                      Файл: {file.original_name} · {file.size_label}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </section>

        <aside className="file-storage-side">
          <h4>Карточка объекта</h4>
          <dl>
            <dt>Файл</dt>
            <dd>{file.original_name}</dd>
            <dt>Версия</dt>
            <dd>
              {archived
                ? `${file.current_version_no} · архив`
                : file.current_version_no || currentVersion?.version_no || '—'}
            </dd>
            <dt>Действует с</dt>
            <dd>
              {currentVersion?.effective_date ||
                (file.created_at ? new Date(file.created_at).toLocaleDateString('ru-RU') : '—')}
            </dd>
            <dt>Источник</dt>
            <dd>{currentVersion?.source || file.folder || 'хранилище'}</dd>
            <dt>Доступ</dt>
            <dd>{file.is_archived ? 'Архив' : 'Актуальный'}</dd>
            <dt>Права</dt>
            <dd>{aclLabel}</dd>
          </dl>

          <button
            type="button"
            className="file-storage-btn primary"
            onClick={() => onDownload(file.file_id)}
          >
            Скачать оригинал
          </button>
          <button
            type="button"
            className="file-storage-btn"
            onClick={() => {
              onCopyLink(file);
            }}
          >
            Копировать ссылку
          </button>
          {onOpenVersions ? (
            <button type="button" className="file-storage-btn" onClick={onOpenVersions}>
              Версии документа
            </button>
          ) : null}
          <button type="button" className="file-storage-btn ghost" onClick={onClose}>
            ← К хранилищу
          </button>
        </aside>
      </div>
    </div>
  );
};
